import React, { useState, useEffect } from 'react';
import { 
  Save, 
  RotateCcw, 
  Eye, 
  AlertTriangle,
  Home,
  Search,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Globe,
  MapPin,
  Mail,
  Phone,
  MessageCircle,
  Calendar,
  Clock,
  User,
  Users,
  Settings,
  Info,
  HelpCircle,
  ExternalLink,
  Download,
  Upload,
  Edit,
  Trash2,
  Plus,
  Minus,
  Check,
  X,
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  EyeOff,
  Lock,
  Unlock,
  Shield,
  Zap,
  Target,
  Flag,
  Bookmark,
  Share,
  Copy,
  Link,
  Image,
  Video,
  Music,
  File,
  Folder,
  Database,
  Server,
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  Printer,
  Camera,
  Mic,
  Headphones,
  Speaker,
  Volume,
  VolumeX,
  Volume1,
  Volume2,
  Play,
  Pause,
  Grid,
  List,
  Menu,
  MoreHorizontal,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  CornerUpLeft,
  CornerUpRight,
  CornerDownLeft,
  CornerDownRight,
  Move,
  RotateCcw as RotateCcwIcon,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { notFoundPageAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import AdminSectionWrapper from './AdminSectionWrapper';

const NotFoundPageManagement = () => {
  const { notify } = useNotificationHook();
  const [pageData, setPageData] = useState({
    title: 'Page Not Found',
    subtitle: 'Oops! The page you are looking for does not exist.',
    description: 'The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.',
    error_code: '404',
    danger_icon: 'AlertTriangle',
    primary_button_text: 'Go Back Home',
    primary_button_icon: 'Home',
    primary_button_link: '/',
    secondary_button_text: 'Search Our Site',
    secondary_button_icon: 'Search',
    search_placeholder: 'Search for pages...',
    background_animation: true,
    decorative_elements: true
  });

  const [availableIcons, setAvailableIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Icon mapping for preview
  const iconMap = {
    AlertTriangle, Home, Search, ArrowLeft, ArrowRight, RefreshCw, Globe, MapPin,
    Mail, Phone, MessageCircle, Calendar, Clock, User, Users,
    Settings, Info, HelpCircle, ExternalLink, Download, Upload,
    Edit, Trash2, Plus, Minus, Check, X, Star, Heart, ThumbsUp,
    ThumbsDown, Eye, EyeOff, Lock, Unlock, Shield, Zap, Target,
    Flag, Bookmark, Share, Copy, Link, Image, Video, Music, File,
    Folder, Database, Server, Monitor, Smartphone, Tablet, Laptop,
    Printer, Camera, Mic, Headphones, Speaker, Volume, VolumeX,
    Volume1, Volume2, Play, Pause, Grid, List, Menu,
    MoreHorizontal, MoreVertical, ChevronLeft, ChevronRight,
    ChevronUp, ChevronDown, ArrowUp, ArrowDown, CornerUpLeft,
    CornerUpRight, CornerDownLeft, CornerDownRight, Move,
    RotateCcwIcon, RotateCw, ZoomIn, ZoomOut, Maximize, Minimize,
    Maximize2, Minimize2
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const response = await notFoundPageAPI.getAdminContent();
      if (response?.data?.success) {
        const data = response.data.data;
        setPageData({
          title: data.title || 'Page Not Found',
          subtitle: data.subtitle || 'Oops! The page you are looking for does not exist.',
          description: data.description || 'The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.',
          error_code: data.error_code || '404',
          danger_icon: data.danger_icon || 'AlertTriangle',
          primary_button_text: data.primary_button_text || 'Go Back Home',
          primary_button_icon: data.primary_button_icon || 'Home',
          primary_button_link: data.primary_button_link || '/',
          secondary_button_text: data.secondary_button_text || 'Search Our Site',
          secondary_button_icon: data.secondary_button_icon || 'Search',
          search_placeholder: data.search_placeholder || 'Search for pages...',
          background_animation: data.background_animation !== false,
          decorative_elements: data.decorative_elements !== false
        });
        setAvailableIcons(data.availableIcons || []);
      }
    } catch (error) {
      console.error('Error fetching Not Found page data:', error);
      notify.error('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPageData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await notFoundPageAPI.updateContent(pageData);
      if (response?.data?.success) {
        notify.success('Not Found page updated successfully!');
      }
    } catch (error) {
      console.error('Error updating Not Found page:', error);
      notify.error('Failed to update page');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all content to defaults?')) {
      try {
        setSaving(true);
        const response = await notFoundPageAPI.resetContent();
        if (response?.data?.success) {
          await fetchPageData();
          notify.success('Page content reset to defaults!');
        }
      } catch (error) {
        console.error('Error resetting Not Found page:', error);
        notify.error('Failed to reset page');
      } finally {
        setSaving(false);
      }
    }
  };

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || AlertTriangle;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Not Found Page Management
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Customize the 404 error page content and appearance
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
            : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <div className="space-y-6">
          {/* Basic Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Basic Content
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Error Code
                </label>
                <input
                  type="text"
                  value={pageData.error_code}
                  onChange={(e) => handleInputChange('error_code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="404"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={pageData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Page Not Found"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={pageData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Oops! The page you are looking for does not exist."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={pageData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  placeholder="The page you are looking for might have been removed, had its name changed, or is temporarily unavailable."
                />
              </div>
            </div>
          </div>

          {/* Icons and Visual Elements */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Icons & Visual Elements
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Danger Icon
                </label>
                <select
                  value={pageData.danger_icon}
                  onChange={(e) => handleInputChange('danger_icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={pageData.background_animation}
                    onChange={(e) => handleInputChange('background_animation', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Background Animation
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={pageData.decorative_elements}
                    onChange={(e) => handleInputChange('decorative_elements', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Decorative Elements
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Primary Button */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Primary Button
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={pageData.primary_button_text}
                  onChange={(e) => handleInputChange('primary_button_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Go Back Home"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Icon
                </label>
                <select
                  value={pageData.primary_button_icon}
                  onChange={(e) => handleInputChange('primary_button_icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Link
                </label>
                <input
                  type="text"
                  value={pageData.primary_button_link}
                  onChange={(e) => handleInputChange('primary_button_link', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="/"
                />
              </div>
            </div>
          </div>

          {/* Secondary Button */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Secondary Button
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={pageData.secondary_button_text}
                  onChange={(e) => handleInputChange('secondary_button_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Search Our Site"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Icon
                </label>
                <select
                  value={pageData.secondary_button_icon}
                  onChange={(e) => handleInputChange('secondary_button_icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Search Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Search Section
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Placeholder
              </label>
              <input
                type="text"
                value={pageData.search_placeholder}
                onChange={(e) => handleInputChange('search_placeholder', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Search for pages..."
              />
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Live Preview
          </h3>
          <div className="bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 rounded-lg p-4 sm:p-6 min-h-[600px]">
            <div className="text-center">
              {/* Error Code */}
              <div className="mb-6">
                <div className="relative inline-block">
                  <div className="text-6xl font-bold text-gray-200 dark:text-gray-700">
                    {pageData.error_code}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    {(() => {
                      const IconComponent = getIconComponent(pageData.danger_icon);
                      return <IconComponent className="w-12 h-12 text-red-500 dark:text-red-400" />;
                    })()}
                  </div>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {pageData.title}
              </h1>

              {/* Subtitle */}
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
                {pageData.subtitle}
              </p>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {pageData.description}
              </p>

              {/* Search Box */}
              <div className="mb-6">
                <div className="relative max-w-md mx-auto">
                  <input
                    type="text"
                    placeholder={pageData.search_placeholder}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
                  {(() => {
                    const IconComponent = getIconComponent(pageData.primary_button_icon);
                    return <IconComponent className="w-4 h-4" />;
                  })()}
                  {pageData.primary_button_text}
                </button>
                
                <button className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
                  {(() => {
                    const IconComponent = getIconComponent(pageData.secondary_button_icon);
                    return <IconComponent className="w-4 h-4" />;
                  })()}
                  {pageData.secondary_button_text}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminSectionWrapper>
  );
};

export default NotFoundPageManagement; 